import { Alert } from '@creditornot/cb-alert';
import { MagnifyingGlass } from '@creditornot/cb-icons';
import { Input } from '@creditornot/cb-input';
import { SegmentedControl } from '@creditornot/cb-segmented-control';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { MOBILE_BREAKPOINT, useIsMobile } from 'common/hooks/useIsMobile';
import { useMediaQuery } from 'common/hooks/useMediaQuery';

import { CoversTab } from './CoversTab';
import { VirtualizedListMenuItems } from './VirtualizedListMenuItems';
import { PanelTabs } from './types';
import { useLinkItems } from './useListItems/useLinkItems';
import { Breakpoints } from '../../constants/breakpoints';
import { PhotoShootHeaderItem, PhotoShootListItem, PhotoShootMenuItem } from '../../types';

export type LinksPanelProps = {
  menuItems: PhotoShootMenuItem[];
  headerItems: PhotoShootHeaderItem[];
  listItems: PhotoShootListItem[];
  isReadOnly?: boolean;
};

const Root = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: 100%;
`;

const TopContainer = styled.div`
  padding: 28px 32px 12px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 12px 8px 8px;
  }
`;

const Tab = styled.div`
  flex: 1;
  min-height: 0;
`;

const PanelNavigation = styled.div`
  display: flex;
  gap: 16px;

  @media (max-width: ${Breakpoints.LAPTOP}px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const StyledAlert = styled(Alert)`
  margin: 0 32px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    margin: 0 8px;
  }
`;

export const LinksPanel = ({ menuItems, headerItems, listItems, isReadOnly = false }: LinksPanelProps) => {
  const [activeTab, setActiveTab] = useState<PanelTabs>(PanelTabs.MENU_ITEMS);
  const { listRef, linkMenuItems, linkListItems, linkHeaderItems, handleSearch } = useLinkItems({
    menuItems,
    headerItems,
    listItems,
    isReadOnly,
  });
  const intl = useIntl();
  const isMobile = useIsMobile();
  const isLaptop = useMediaQuery(`(min-width: ${Breakpoints.MOBILE + 1}px) and (max-width: ${Breakpoints.LAPTOP}px)`);
  const areTabsEnabled = linkListItems.length > 0 && linkHeaderItems.length > 0;

  return (
    <Root>
      <TopContainer>
        <PanelNavigation>
          {areTabsEnabled && (
            <SegmentedControl<PanelTabs>
              key={intl.locale}
              options={[
                { label: intl.formatMessage({ id: 'photo-shoot.photo-link.items' }), value: PanelTabs.MENU_ITEMS },
                { label: intl.formatMessage({ id: 'photo-shoot.photo-link.covers' }), value: PanelTabs.COVERS },
              ]}
              value={activeTab}
              onChange={setActiveTab}
              fixedWidths={isLaptop || isMobile}
            />
          )}

          {menuItems.length > 0 && (
            <Input
              onChange={handleSearch}
              placeholder={intl.formatMessage({ id: 'photo-shoot.photo-link.search-placeholder' })}
              icon={<MagnifyingGlass />}
              disabled={activeTab !== PanelTabs.MENU_ITEMS}
            />
          )}
        </PanelNavigation>
      </TopContainer>

      <Tab>
        {activeTab === PanelTabs.MENU_ITEMS &&
          (menuItems.length > 0 ? (
            <VirtualizedListMenuItems items={linkMenuItems} ref={listRef} />
          ) : (
            <StyledAlert variant="info">
              <FormattedMessage id="photo-shoot.photo-link.no-items" />
            </StyledAlert>
          ))}

        {activeTab === PanelTabs.COVERS && <CoversTab headerItems={linkHeaderItems} listItems={linkListItems} />}
      </Tab>
    </Root>
  );
};
